import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { createAction, props, on, createSelector } from '@ngrx/store';
import { EntityStoreFactory, IGenericEntityState } from '@pit/core-store';
import { IDtoShipmentProcess } from '@pit/frontend-model';
import { EntityDataServiceAccessorService } from '@pit/core-data';
import { IDtoShipmentProcessEntityDataService } from '../../module.data/services/interfaces/dto.shipment.process.entity.data.service.interface';

export class DtoShipmentProcessStoreFactory extends EntityStoreFactory<IDtoShipmentProcess> {

    private static _instance: DtoShipmentProcessStoreFactory = null;

    public static get instance(): DtoShipmentProcessStoreFactory {
        if (this._instance === null) {
            this._instance = new DtoShipmentProcessStoreFactory();
        }

        return this._instance;
    }

    constructor() {
        super('DtoShipmentProcess', 'dto.shipment.process');
    }

    protected override doCreateActions(): void {
        super.doCreateActions();

        this.entityActions.add('get.active.by.depot', createAction(
            'Get Active ShipmentProcesses For Depot',
            props<{ depotNumber: string }>()
        ));
        this.entityActions.add('get.active.by.depot.success', createAction(
            'Get Active ShipmentProcesses For Depot Success',
            props<{ items: IDtoShipmentProcess[] }>()
        ));
        this.entityActions.add('get.active.by.depot.failure', createAction(
            'Get Active ShipmentProcesses For Depot Failure',
            props<{ error: any }>()
        ));

        this.entityActions.add('get.returns.by.depot', createAction(
            'Get Returns ShipmentProcesses For Depot',
            props<{ depotNumber: string, includeInDelivery: boolean }>()
        ));
        this.entityActions.add('get.returns.by.depot.success', createAction(
            'Get Returns ShipmentProcesses For Depot Success',
            props<{ items: IDtoShipmentProcess[] }>()
        ));
        this.entityActions.add('get.returns.by.depot.failure', createAction(
            'Get Returns ShipmentProcesses For Depot Failure',
            props<{ error: any }>()
        ));
    }

    protected override doCreateEffects(): void {
        super.doCreateEffects();

        this.entityEffects.add('get.active.by.depot', {
            effect: (actions$: Actions, dataServiceAccessor: EntityDataServiceAccessorService) => {
                return createEffect(() =>
                    actions$.pipe(
                        ofType(this.getAction('get.active.by.depot')),
                        map(action => action),
                        switchMap(action =>
                            (dataServiceAccessor.getEntityDataService<IDtoShipmentProcess>(this.dataAccessName) as IDtoShipmentProcessEntityDataService)
                                .getActiveShipmentProcessesForDepot$(action.depotNumber).pipe(
                                    map(items => this.getAction('get.active.by.depot.success')({ items: items })),
                                    catchError(error => of(this.getAction('get.active.by.depot.failure')({ error: error })))
                                )
                        )
                    )
                );
            }
        });

        this.entityEffects.add('get.returns.by.depot', {
            effect: (actions$: Actions, dataServiceAccessor: EntityDataServiceAccessorService) => {
                return createEffect(() =>
                    actions$.pipe(
                        ofType(this.getAction('get.returns.by.depot')),
                        map(action => action),
                        switchMap(action =>
                            (dataServiceAccessor.getEntityDataService<IDtoShipmentProcess>(this.dataAccessName) as IDtoShipmentProcessEntityDataService)
                                .getShipmentProcessesToReturnForDepot$(action.depotNumber, action.includeInDelivery).pipe(
                                    map(items => this.getAction('get.returns.by.depot.success')({ items: items })),
                                    catchError(error => of(this.getAction('get.returns.by.depot.failure')({ error: error })))
                                )
                        )
                    )
                );
            }
        });
    }

    protected override doCreateSelectors() {
        super.doCreateSelectors();
    }

    protected override doGetReducers<S extends IGenericEntityState<IDtoShipmentProcess>>(): any {
        const result: any = super.doGetReducers();

        result.push(
            on(
                this.getAction('get.active.by.depot'),
                this.getAction('get.returns.by.depot'),
                (state: any, { showLoadingIndicator }) => {
                    return {
                        ...state,
                        loading: ((showLoadingIndicator === true) ? true : state.loading)
                    };
                }
            )
        );
        result.push(
            on(
                this.getAction('get.active.by.depot.failure'),
                this.getAction('get.returns.by.depot.failure'),
                (state: any) => {
                    return {
                        ...state,
                        loading: ((state.loading === true) ? false : state.loading)
                    };
                }
            )
        );
        result.push(
            on(
                this.getAction('get.active.by.depot.success'),
                this.getAction('get.returns.by.depot.success'),
                (state: any, { items }) => {
                    const res = this.entityAdapter.setAll(items, {
                        ...state,
                        loading: false,
                        selectedItem: null
                    });
                    return res;
                })
        );

        return result;
    }

}
